import React from 'react';
import DialogueLesson, { Character, DialogueNode } from './DialogueLesson';

// ============ CHARACTERS ============

const characters: Character[] = [
  {
    id: 'maya',
    name: 'Maya',
    avatar: '👩‍🎓',
    color: '#3b82f6',
    position: 'left',
  },
  {
    id: 'professor',
    name: 'Professor Chen',
    avatar: '👨‍🏫',
    color: '#10b981',
    position: 'right',
  },
];

// ============ LESSON CONTENT ============
// Year 8 English: Persuasive Techniques in Advertising

const nodes: DialogueNode[] = [
  // Introduction
  {
    id: 'intro-1',
    type: 'chapter-break',
    chapterNumber: 1,
    chapterTitle: 'The Mystery Advertisement',
  },
  {
    id: 'maya-1',
    type: 'dialogue',
    characterId: 'maya',
    text: "Professor Chen! I found this weird ad online and I can't tell if it's trying to trick me or not...",
    emotion: 'confused',
  },
  {
    id: 'prof-1',
    type: 'dialogue',
    characterId: 'professor',
    text: "Ah, Maya! Perfect timing. You've stumbled upon one of the most important skills in Year 8 English - recognizing persuasive techniques. Let me see what you've found.",
    emotion: 'thinking',
  },
  {
    id: 'narration-1',
    type: 'narration',
    text: 'Maya shows Professor Chen an advertisement for a new energy drink called "POWER SURGE"...',
  },
  {
    id: 'reveal-ad',
    type: 'reveal',
    revealTitle: 'The Advertisement',
    revealContent: '"POWER SURGE: The drink of champions! 9 out of 10 athletes agree - nothing beats the rush! Don\'t be the only one missing out. LIMITED TIME: Buy now and join the elite!"',
  },
  {
    id: 'prof-2',
    type: 'dialogue',
    characterId: 'professor',
    text: "Fascinating! This advertisement is packed with persuasive techniques. Let's analyze it together like detectives. Can you spot the first technique?",
    emotion: 'excited',
  },

  // First Question - Bandwagon
  {
    id: 'q1',
    type: 'question-mc',
    questionText: 'What persuasive technique is being used in "Don\'t be the only one missing out"?',
    options: [
      { id: 'a', text: 'Repetition' },
      { id: 'b', text: 'Bandwagon Appeal' },
      { id: 'c', text: 'Metaphor' },
      { id: 'd', text: 'Alliteration' },
    ],
    correctOptionId: 'b',
    explanation: 'Bandwagon Appeal creates fear of being left out. It suggests everyone else is doing something, so you should too!',
    xpReward: 20,
  },
  {
    id: 'maya-2',
    type: 'dialogue',
    characterId: 'maya',
    text: "So they're trying to make me feel like I'll be left out if I don't buy it? That's sneaky!",
    emotion: 'thinking',
  },
  {
    id: 'prof-3',
    type: 'dialogue',
    characterId: 'professor',
    text: "Exactly! It's called the Bandwagon Effect - the idea that 'everyone is doing it, so you should too.' Advertisers use our natural desire to belong to pressure us into decisions.",
    emotion: 'neutral',
  },

  // Concept Reveal
  {
    id: 'reveal-bandwagon',
    type: 'reveal',
    revealTitle: 'Bandwagon Appeal',
    revealContent: 'A persuasive technique that encourages people to do something because "everyone else is doing it." It plays on our fear of missing out (FOMO) and our desire to fit in with the crowd.',
  },

  // Chapter 2 - Statistics
  {
    id: 'intro-2',
    type: 'chapter-break',
    chapterNumber: 2,
    chapterTitle: 'The Numbers Game',
  },
  {
    id: 'prof-4',
    type: 'dialogue',
    characterId: 'professor',
    text: 'Now, look at this part: "9 out of 10 athletes agree." What questions should we ask about this statistic?',
    emotion: 'thinking',
  },
  {
    id: 'q2',
    type: 'question-mc',
    questionText: 'What makes this statistic potentially misleading?',
    options: [
      { id: 'a', text: 'We don\'t know who funded the survey' },
      { id: 'b', text: 'We don\'t know how many athletes were surveyed' },
      { id: 'c', text: 'We don\'t know what they agreed to' },
      { id: 'd', text: 'All of the above' },
    ],
    correctOptionId: 'd',
    explanation: 'Statistics can be manipulated! Always ask: Who conducted the research? How many people? What exactly was the question? A survey of 10 athletes paid by the company is very different from an independent study of 1000!',
    xpReward: 25,
  },
  {
    id: 'maya-3',
    type: 'dialogue',
    characterId: 'maya',
    text: 'Wait... so if they only asked 10 athletes, and 9 agreed, that sounds impressive, but it could just be their friends!',
    emotion: 'excited',
  },
  {
    id: 'prof-5',
    type: 'dialogue',
    characterId: 'professor',
    text: "You're becoming quite the critical thinker, Maya! This is called 'Appeal to Statistics' - using numbers to seem credible. But without context, numbers can be meaningless.",
    emotion: 'happy',
  },

  // Chapter 3 - Emotive Language
  {
    id: 'intro-3',
    type: 'chapter-break',
    chapterNumber: 3,
    chapterTitle: 'Words That Feel',
  },
  {
    id: 'prof-6',
    type: 'dialogue',
    characterId: 'professor',
    text: "Let's examine the language: 'champions,' 'elite,' 'rush.' These aren't random choices. What do these words make you feel?",
    emotion: 'thinking',
  },
  {
    id: 'q3',
    type: 'question-input',
    questionText: 'What is the term for language specifically chosen to trigger emotional responses in the audience? (Two words, starts with "E")',
    correctAnswer: 'emotive language',
    explanation: 'Emotive Language uses words that deliberately trigger emotional responses. Words like "elite," "champion," and "rush" are chosen to make you feel excited and powerful!',
    xpReward: 25,
  },
  {
    id: 'maya-4',
    type: 'dialogue',
    characterId: 'maya',
    text: 'The words make me feel like drinking this would make me special, like a champion athlete!',
    emotion: 'excited',
  },
  {
    id: 'prof-7',
    type: 'dialogue',
    characterId: 'professor',
    text: "Precisely! They've associated their product with feelings of success and exclusivity. You're not just buying a drink - you're buying the feeling of being elite.",
    emotion: 'neutral',
  },

  // Reveal - Emotive Language
  {
    id: 'reveal-emotive',
    type: 'reveal',
    revealTitle: 'Emotive Language',
    revealContent: 'Words and phrases chosen specifically for their emotional impact rather than their literal meaning. Advertisers use emotive language to bypass our logical thinking and appeal directly to our feelings.',
  },

  // Chapter 4 - Urgency
  {
    id: 'intro-4',
    type: 'chapter-break',
    chapterNumber: 4,
    chapterTitle: 'The Pressure Cooker',
  },
  {
    id: 'prof-8',
    type: 'dialogue',
    characterId: 'professor',
    text: 'One final technique - notice "LIMITED TIME: Buy now." Why do you think they include this?',
    emotion: 'thinking',
  },
  {
    id: 'q4',
    type: 'question-mc',
    questionText: 'What is the purpose of "LIMITED TIME: Buy now"?',
    options: [
      { id: 'a', text: 'To inform about actual scarcity' },
      { id: 'b', text: 'To create artificial urgency' },
      { id: 'c', text: 'To provide clear instructions' },
      { id: 'd', text: 'To follow advertising regulations' },
    ],
    correctOptionId: 'b',
    explanation: 'Creating Urgency pressures people to act quickly before they can think critically. "Limited time" creates fear that you\'ll miss out if you don\'t act NOW!',
    xpReward: 20,
  },
  {
    id: 'maya-5',
    type: 'dialogue',
    characterId: 'maya',
    text: "They want me to buy it before I have time to research or ask questions! That's why sales always say 'ending soon'!",
    emotion: 'excited',
  },
  {
    id: 'prof-9',
    type: 'dialogue',
    characterId: 'professor',
    text: "Brilliant deduction! It's called 'Creating Urgency' - one of the most powerful sales tactics. If they can stop you from thinking, they can stop you from questioning.",
    emotion: 'happy',
  },

  // Summary Question
  {
    id: 'q5',
    type: 'question-mc',
    questionText: 'Which combination of techniques did this advertisement use?',
    options: [
      { id: 'a', text: 'Bandwagon, Statistics, Emotive Language, Urgency' },
      { id: 'b', text: 'Repetition, Alliteration, Metaphor, Simile' },
      { id: 'c', text: 'Hyperbole, Rhetorical Question, Rule of Three' },
      { id: 'd', text: 'Anecdote, Expert Opinion, Facts Only' },
    ],
    correctOptionId: 'a',
    explanation: 'The ad used: Bandwagon ("don\'t miss out"), Statistics ("9 out of 10"), Emotive Language ("champions," "elite"), and Urgency ("limited time").',
    xpReward: 30,
  },

  // Conclusion
  {
    id: 'intro-5',
    type: 'chapter-break',
    chapterNumber: 5,
    chapterTitle: 'The Power of Awareness',
  },
  {
    id: 'maya-6',
    type: 'dialogue',
    characterId: 'maya',
    text: "Wow, that one advertisement had four different manipulation techniques! How do I protect myself from these tricks?",
    emotion: 'thinking',
  },
  {
    id: 'prof-10',
    type: 'dialogue',
    characterId: 'professor',
    text: "You've already started, Maya. By recognizing these techniques, you've broken their power over you. Knowledge is your shield!",
    emotion: 'happy',
  },

  // Final Reveal
  {
    id: 'reveal-final',
    type: 'reveal',
    revealTitle: 'Critical Thinking Checklist',
    revealContent: 'When you see an advertisement, ask: 1) What emotions is it trying to trigger? 2) What evidence supports the claims? 3) Why do they want me to act quickly? 4) Who benefits if I believe this?',
  },

  {
    id: 'prof-11',
    type: 'dialogue',
    characterId: 'professor',
    text: "Remember Maya - persuasive techniques aren't inherently bad. Writers use them in essays to convince readers of important ideas. The key is recognizing them so YOU control your decisions, not the advertiser.",
    emotion: 'neutral',
  },
  {
    id: 'maya-7',
    type: 'dialogue',
    characterId: 'maya',
    text: "Thanks Professor Chen! I'm never looking at ads the same way again. And I can use these techniques in my own persuasive writing too!",
    emotion: 'happy',
  },
  {
    id: 'prof-12',
    type: 'dialogue',
    characterId: 'professor',
    text: "Exactly! That's the beauty of understanding persuasion - it makes you both a better reader AND a better writer. Well done today, Maya!",
    emotion: 'excited',
  },

  // Completion
  {
    id: 'complete',
    type: 'complete',
  },
];

// ============ COMPONENT ============

interface Year8EnglishLessonProps {
  onClose?: () => void;
}

const Year8EnglishLesson: React.FC<Year8EnglishLessonProps> = ({ onClose }) => {
  return (
    <DialogueLesson
      title="Persuasive Techniques in Advertising"
      subject="english"
      characters={characters}
      nodes={nodes}
      onClose={onClose}
    />
  );
};

export default Year8EnglishLesson;
